// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withHandlers, compose } from '../../../../../../../node_module_clones/recompose';
import { createSelector } from 'reselect';
import { uniq, isEmpty } from 'lodash/fp';

// Utils
import { prop } from '../../../../../../../common/utils/immutableHelper';
import { getGroupPopupId } from './notificationOptOutPopupUtils';
import { getUserId } from '../../../../../../../common/users/utils/userPropertyUtils';
import { getBoardNotificationPreference } from '../../../../../../notifications/preferences/boardNotificationPreferenceUtils';
import { getGroupId, getGroupNotifications } from '../../groups/notificationGroupUtils';
import {
    getNotificationElementId,
    getNotificationId,
} from '../../../../../../../common/notifications/notificationModelUtils';

// Selectors
import { activePopupSelector } from '../../../../../../components/popupPanel/popupOpenSelector';
import { getElements } from '../../../../../../element/selectors/elementsSelector';
import { getAllBoardNotificationPreferences } from '../../../../../../notifications/preferences/boardNotificationPreferencesSelector';
import { getSelectedNotificationData } from '../notificationsPopupSelector';

// Actions
import { closePopup } from '../../../../../../components/popupPanel/popupActions';
import { removeNotifications } from '../../../../../../../common/notifications/notificationActions';
import { unsubscribeFromMultipleBoards } from '../../../../../../notifications/preferences/boardNotificationPreferencesActions';

// Components
import PopupPanelMouseManager from '../../../../../../components/popupPanel/PopupPanelMouseManager';
import FloatingPanel from '../../../../../../components/tooltips/FloatingPanel';
import LinkButton from '../../../../../../components/buttons/LinkButton';

// Constants
import { TooltipPositions } from '../../../../../../components/tooltips/tooltipConstants';

// Styles
import './NotificationOptOutPopup.scss';

const getBoardIdsFromNotificationsGroup = (group) => {
    const notifications = getGroupNotifications(group);
    return uniq(notifications.map(getNotificationElementId));
};

const getNotificationIdsFromNotificationsGroup = (group) => {
    const notifications = getGroupNotifications(group);
    return notifications.map(getNotificationId);
};

const mapStateToProps = createSelector(
    activePopupSelector,
    getSelectedNotificationData,
    getElements,
    getAllBoardNotificationPreferences,
    (activePopupState, selectedNotificationData, elements, boardNotificationPreferences) => {
        if (!selectedNotificationData) return { popupOpen: false };

        const group = prop('group', selectedNotificationData);
        const popupId = getGroupPopupId(group);
        const popupOpen = activePopupState.has(popupId);

        return {
            popupOpen,
            boardNotificationsSubscribed: getBoardNotificationPreference({
                elementId: getGroupId(group),
                elements,
                boardNotificationPreferences,
            }),
            ...(selectedNotificationData && selectedNotificationData.toJS()),
        };
    },
);

const mapDispatchToProps = (dispatch) => ({
    dispatchClosePopup: (popupId) => dispatch(closePopup(popupId, false)),
    dispatchUnsubscribeFromNotification: (props) => {
        const { group, currentUser } = props;

        const userId = getUserId(currentUser);
        const boardIds = getBoardIdsFromNotificationsGroup(group);
        const notificationIds = getNotificationIdsFromNotificationsGroup(group);

        dispatch(unsubscribeFromMultipleBoards({ userId, boardIds }));

        if (!isEmpty(notificationIds)) dispatch(removeNotifications({ userId, notificationIds }));

        props.dispatchClosePopup(getGroupPopupId(group));
    },
});

const enhance = compose(
    connect(mapStateToProps, mapDispatchToProps),
    withHandlers({
        turnOffNotifications: (props) => () => props.dispatchUnsubscribeFromNotification(props),
        closePopup:
            ({ group, dispatchClosePopup }) =>
            () =>
                dispatchClosePopup(getGroupPopupId(group)),
    }),
);

const NotificationOptOutPopup = (props) => {
    const { popupOpen, group, turnOffNotifications, boardNotificationsSubscribed } = props;

    if (!popupOpen) return null;

    const text = boardNotificationsSubscribed ? 'Stop following this board' : 'Delete this notification';

    const popupId = getGroupPopupId(group);

    return (
        <FloatingPanel
            selector={`#${getGroupPopupId(group)}`}
            className="NotificationOptOutPopup light"
            arrowWidth={13}
            arrowHeight={8}
            distance={0}
            position={TooltipPositions.BOTTOM}
            pollPosition
        >
            <PopupPanelMouseManager {...props} visible popupId={popupId}>
                <div className="popup-content">
                    <div className="actions">
                        <LinkButton onClick={turnOffNotifications}>{text}</LinkButton>
                    </div>
                </div>
            </PopupPanelMouseManager>
        </FloatingPanel>
    );
};

NotificationOptOutPopup.propTypes = {
    popupOpen: PropTypes.bool,
    group: PropTypes.object,
    boardId: PropTypes.string,
    boardNotificationsSubscribed: PropTypes.bool,
    turnOffNotifications: PropTypes.func,
    dispatchUnsubscribeFromNotification: PropTypes.func,
    closePopup: PropTypes.func,
};

export default enhance(NotificationOptOutPopup);
